/**
 * Tabla de selección de items para transferencia EPP
 * Muestra todos los items de la bodega origen con checkboxes y control de cantidades
 */
import React, { useState, useEffect, useMemo } from 'react';
import { Table, Input, Row, Col, Label, Spinner, Badge } from 'reactstrap';
import SimpleBar from 'simplebar-react';
import { useFormikContext } from 'formik';
import { getWarehouseItemList } from '../../../../../helpers/warehouse/backend_helper';
import { toast } from 'react-toastify';

export const TransferenciaSelectorTable = ({ onItemsSelected }) => {
	const validation = useFormikContext();
	const warehouseOriginId = validation.values.warehouse_origin_id;
	const warehouseOriginCode = validation.values._warehouse_origin_code;

	const [items, setItems] = useState([]);
	const [selectedItems, setSelectedItems] = useState({});
	const [loading, setLoading] = useState(false);
	const [phraseFilter, setPhraseFilter] = useState('');

	// Cargar items cuando cambia bodega origen
	useEffect(() => {
		if (warehouseOriginId) {
			loadItems();
		} else {
			setItems([]);
			setSelectedItems({});
		}
	}, [warehouseOriginId]);

	const loadItems = async () => {
		setLoading(true);
		try {
			const response = await getWarehouseItemList({
				warehouse_id: warehouseOriginId,
				all: false,
			});
			setItems(response || []);
		} catch (error) {
			console.error('[TransferenciaSelectorTable] Error al cargar items:', error);
			toast.error('Error al cargar items de la bodega origen');
		} finally {
			setLoading(false);
		}
	};

	// Notificar cambios al padre
	useEffect(() => {
		const transformedItems = Object.entries(selectedItems).map(([id, data]) => {
			const item = items.find(i => i._id === id);
			return {
				NmbItem: item.name,
				DscItem: item.description || '',
				QtyItem: data.quantity,
				UnmdItem: (item.unit_measurement?.name || 'UNI').toUpperCase(),
				VlrCodigo: item.code_sap || '',
				TpoCodigo: 'INT1',
				PrcItem: item.price || 0,
				MontoItem: (item.price || 0) * data.quantity,
				_module_vars: {
					warehouse_item_id: item._id,
					warehouse_code: item.warehouse?.code_sap || warehouseOriginCode || '',
					warehouse_group_id: item.warehouse_group_id || '',
					warehouse_group_code: item.warehouse_group?.code_sap || '',
					warehouse_category_name: item.warehouse_category?.name || '',
					size: item.size || '',
					color: item.color || '',
					epp_qty_max: item.available,
				},
			};
		});
		onItemsSelected && onItemsSelected(transformedItems);
	}, [selectedItems, items, warehouseOriginCode]);

	const handleItemSelect = itemId => {
		setSelectedItems(prev => {
			const next = { ...prev };
			if (next[itemId]) {
				delete next[itemId];
			} else {
				next[itemId] = { quantity: 1 };
			}
			return next;
		});
	};

	const handleQuantityChange = (itemId, value, available) => {
		const quantity = Math.max(1, Math.min(available, Number(value) || 1));
		setSelectedItems(prev => ({
			...prev,
			[itemId]: { ...prev[itemId], quantity },
		}));
	};

	const filteredItems = useMemo(() => {
		return items.filter(
			item =>
				item.available > 0 &&
				(!phraseFilter ||
					item.name?.toLowerCase().includes(phraseFilter.toLowerCase()) ||
					item.code_sap?.toLowerCase().includes(phraseFilter.toLowerCase()))
		);
	}, [items, phraseFilter]);

	if (!warehouseOriginId) {
		return (
			<Alert color='info' className='mb-0'>
				Primero seleccione una bodega fiscal de origen en la sección de destino.
			</Alert>
		);
	}

	return (
		<div className='transferencia-selector-table'>
			{/* Filtro por frase */}
			<Row className='mb-3 align-items-end'>
				<Col lg={8}>
					<Label className='form-label'>Filtrar por nombre o código SAP</Label>
					<Input
						type='text'
						className='form-control'
						placeholder='Buscar item...'
						value={phraseFilter}
						onChange={e => setPhraseFilter(e.target.value)}
					/>
				</Col>
				<Col lg={4} className='text-end'>
					{loading && <Spinner size='sm' className='text-primary' />}
					{!loading && (
						<Badge color='info' className='p-2'>
							{filteredItems.length} items disponibles
						</Badge>
					)}
				</Col>
			</Row>

			{/* Items seleccionados (Resumen rápido) */}
			{Object.keys(selectedItems).length > 0 && (
				<div className='mb-4 p-3 bg-light rounded border'>
					<h6 className='mb-3 d-flex justify-content-between align-items-center'>
						Items Seleccionados
						<Badge color='success' pill>
							{Object.keys(selectedItems).length}
						</Badge>
					</h6>
					<SimpleBar style={{ maxHeight: '180px' }} className='pe-2'>
						<Table bordered size='sm' striped hover className='mb-0'>
							<thead className='table-light sticky-top'>
								<tr>
									<th className='text-center' style={{ width: '40px' }}>
										#
									</th>
									<th>NOMBRE</th>
									<th className='text-center' style={{ width: '100px' }}>
										DISP.
									</th>
									<th className='text-center' style={{ width: '120px' }}>
										CANTIDAD
									</th>
								</tr>
							</thead>
							<tbody>
								{Object.entries(selectedItems).map(([id, data]) => {
									const item = items.find(i => i._id === id);
									if (!item) return null;
									return (
										<tr key={`selected-${id}`}>
											<td className='text-center'>
												<Input
													type='checkbox'
													className='form-check-input'
													checked
													onChange={() => handleItemSelect(id)}
												/>
											</td>
											<td className='small'>{item.name}</td>
											<td className='text-center small'>
												{item.available}
											</td>
											<td>
												<div className='input-step input-step-sm w-100 justify-content-center'>
													<button
														type='button'
														className='minus'
														onClick={() =>
															handleQuantityChange(
																id,
																data.quantity - 1,
																item.available
															)
														}
													>
														–
													</button>
													<Input
														type='number'
														className='product-quantity'
														value={data.quantity}
														min='1'
														max={item.available}
														onChange={e =>
															handleQuantityChange(
																id,
																e.target.value,
																item.available
															)
														}
													/>
													<button
														type='button'
														className='plus'
														onClick={() =>
															handleQuantityChange(
																id,
																data.quantity + 1,
																item.available
															)
														}
													>
														+
													</button>
												</div>
											</td>
										</tr>
									);
								})}
							</tbody>
						</Table>
					</SimpleBar>
				</div>
			)}

			{/* Tabla principal de items */}
			<div className='border rounded overflow-hidden'>
				<SimpleBar style={{ maxHeight: '350px' }}>
					<Table bordered hover striped className='mb-0'>
						<thead className='table-light sticky-top'>
							<tr>
								<th className='text-center' style={{ width: '40px' }}>
									#
								</th>
								<th>NOMBRE</th>
								<th>CÓDIGO SAP</th>
								<th className='text-center'>DISP.</th>
								<th className='text-center'>UNIDAD</th>
							</tr>
						</thead>
						<tbody>
							{filteredItems.length === 0 ? (
								<tr>
									<td colSpan='5' className='text-center py-4 text-muted'>
										{loading
											? 'Cargando catálogo...'
											: 'No se encontraron items en esta bodega'}
									</td>
								</tr>
							) : (
								filteredItems
									.filter(item => !selectedItems[item._id])
									.map(item => (
										<tr
											key={item._id}
											onClick={() => handleItemSelect(item._id)}
											style={{ cursor: 'pointer' }}
										>
											<td className='text-center'>
												<Input
													type='checkbox'
													className='form-check-input'
													checked={!!selectedItems[item._id]}
													readOnly
												/>
											</td>
											<td className='small'>{item.name}</td>
											<td className='small'>{item.code_sap || '-'}</td>
											<td className='text-center small'>
												{item.available}
											</td>
											<td className='text-center small text-uppercase'>
												{item.unit_measurement?.name || 'UNI'}
											</td>
										</tr>
									))
							)}
						</tbody>
					</Table>
				</SimpleBar>
			</div>
		</div>
	);
};

